import { Injectable, NgZone } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { SsoValidateTokenService } from '../../core/services/sso-validate-token/sso-validate-token.service';
import {
  SsoVerifyTokenAction,
  SsoRefreshTokenAction,
  SsoLogoutAction,
  SsoClearStateAction,
  SsoSetUserInfoAction,
  SsoResetUserInfo,
} from './sso.actions';
import {
  IDecodedToken,
  IUserDecodedToken,
} from '../../core/interfaces/sso.interface';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { EnovaAlertsComponent } from '@/app/core/utils/enova-alerts/enova-alerts.component';
import { tap, catchError, throwError } from 'rxjs';

export class SsoStateModel {
  user: IUserDecodedToken | null;
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
}

@State<SsoStateModel>({
  name: 'sso',
  defaults: {
    user: null,
    token: null,
    refreshToken: null,
    userId: null,
  },
})
@Injectable()
export class SsoState {
  @Selector()
  static userData(state: SsoStateModel): IUserDecodedToken | null {
    return state?.user ?? null;
  }

  @Selector()
  static token(state: SsoStateModel): string | null {
    return state?.token ?? null;
  }

  @Selector()
  static refreshToken(state: SsoStateModel): string | null {
    return state?.refreshToken ?? null;
  }

  @Selector()
  static isAuthenticated(state: SsoStateModel): boolean {
    return !!state?.token && !!state?.user;
  }

  @Selector()
  static userRole(state: SsoStateModel): string {
    return state?.user?.roles[0]?.toLowerCase() ?? '';
  }

  @Selector()
  static userId(state: SsoStateModel): string | null {
    return state?.userId ?? null;
  }

  constructor(
    private ssoService: SsoValidateTokenService,
    public dialog: MatDialog,
    private ngZone: NgZone,
    private router: Router
  ) {}

  @Action(SsoVerifyTokenAction)
  verifyToken(
    ctx: StateContext<SsoStateModel>,
    { payload }: SsoVerifyTokenAction
  ) {
    this.ssoService.verifyToken(payload.accessToken).subscribe({
      next: response => {
        if (response) {
          const role = ctx.getState().user?.roles[0]?.toLowerCase();

          if (role) {
            ctx.patchState({
              userId: response.userId,
            });

            // Agregar un pequeño delay para asegurar que el estado se propague
            setTimeout(() => {
              this.ngZone.run(() => {
                // Navegar a /home y limpiar query parameters
                this.router.navigate(['/home'], {
                  replaceUrl: true, // Esto reemplaza la URL actual en el historial
                });
              });
            }, 100); // 100ms delay
          } else {
            console.error('❌ No se encontró rol del usuario');
          }
        }
      },
      error: error => {
        this.dialog.open(EnovaAlertsComponent, {
          data: {
            title: 'Error',
            message: error.error.message || 'Error al verificar el token',
            type: 'error',
          },
        });
      },
    });
  }

  @Action(SsoSetUserInfoAction)
  setUserInfo(ctx: StateContext<SsoStateModel>, action: SsoSetUserInfoAction) {
    try {
      const decoded = jwtDecode(action.payload.accessToken) as IDecodedToken;

      ctx.patchState({
        user: decoded.user,
        token: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
      });

      // Solo verificar el token si no viene de sincronización cross-tab
      if (!action.skipVerification) {
        ctx.dispatch(new SsoVerifyTokenAction(action.payload));
      } else {
        console.log(
          '⏭️ [SsoState] Verificación de token omitida (sincronización cross-tab)'
        );
      }
    } catch (error) {
      this.dialog.open(EnovaAlertsComponent, {
        data: {
          title: 'Error',
          message: 'Token inválido',
          type: 'error',
        },
      });
    }
  }

  @Action(SsoRefreshTokenAction)
  refreshToken(
    ctx: StateContext<SsoStateModel>,
    { payload }: SsoRefreshTokenAction
  ) {
    return this.ssoService.refreshToken(payload).pipe(
      tap(response => {
        if (response && response.accessToken) {
          try {
            const decoded = jwtDecode(response.accessToken) as IDecodedToken;

            ctx.patchState({
              user: decoded.user,
              token: response.accessToken,
              refreshToken: response.refreshToken,
            });
          } catch (error) {
            throw error;
          }
        } else {
          throw new Error('Invalid refresh response');
        }
      }),
      catchError(error => {
        this.dialog.open(EnovaAlertsComponent, {
          data: {
            title: 'Error',
            message:
              error.error?.message ||
              error.message ||
              'Error al refrescar el token',
            type: 'error',
          },
        });

        // Limpiar estado en caso de error
        ctx.dispatch(new SsoClearStateAction());

        return throwError(() => error);
      })
    );
  }

  @Action(SsoLogoutAction)
  logout(ctx: StateContext<SsoStateModel>, { payload }: SsoLogoutAction) {
    return this.ssoService.logout(payload).subscribe({
      next: () => {
        this.ngZone.run(() => {
          ctx.dispatch(new SsoClearStateAction());
          this.router.navigate(['/']);
        });
      },
      error: error => {
        this.dialog.open(EnovaAlertsComponent, {
          data: {
            title: 'Error',
            message:
              error.error?.message || error.message || 'Error al cerrar sesión',
            type: 'error',
          },
        });
        // Aunque haya error en el servidor, limpiamos el estado local
        this.ngZone.run(() => {
          ctx.dispatch(new SsoClearStateAction());
          this.router.navigate(['/']);
        });
      },
    });
  }

  @Action(SsoClearStateAction)
  clearState(ctx: StateContext<SsoStateModel>) {
    ctx.patchState({
      user: null,
      token: null,
      refreshToken: null,
    });
  }

  @Action(SsoResetUserInfo)
  resetUserInfo(ctx: StateContext<SsoStateModel>) {
    ctx.patchState({
      user: null,
      token: null,
      refreshToken: null,
    });
  }
}
